import React, { useState, useEffect } from 'react';
import {
  Minus,
  Plus,
  ChefHat,
  MessageCircle,
  Utensils,
  ShoppingCart,
  X,
  Check
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/slices/cartSlice';
// Import toast and its styles
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const ShawarmaComponent = () => {
  const dispatch = useDispatch();

  // Local state for tracking selections (quantity and ingredient customizations)
  const [selections, setSelections] = useState({});
  // Modal state for ingredient customization
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Modal state for chef note
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [chefComment, setChefComment] = useState('');
  // For tracking which shawarma item is being customized
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [shawarmas, setShawarma] = useState([]);

  useEffect(() => {
    const docRef = doc(db, 'menuCategories', 'Shawarma');
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setShawarma(docSnapshot.data().items || []);
        } else {
          setShawarma([]);
        }
      },
      (error) => {
        console.error("Error fetching Shawarma data:", error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Opens the customization modal for a given shawarma item.
  const handleOpenModal = (itemId) => {
    const item = shawarmas.find(item => item.id === itemId);
    setSelectedItem(item);
    setSelectedItemId(itemId);
    setIsModalOpen(true);
  };

  // Closes the customization modal.
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItemId(null);
    setSelectedItem(null);
  };

  // Toggles ingredient selection for a shawarma item.
  const handleIngredientChange = (itemId, ingredient, isChecked) => {
    const ingredientInfo = selectedItem.extras.ingredients.find(i => i.name === ingredient.name);
    setSelections(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        extras: {
          ...prev[itemId]?.extras || {},
          ingredients: isChecked
            ? [...(prev[itemId]?.extras?.ingredients || []), { ...ingredientInfo, quantity: 1 }]
            : (prev[itemId]?.extras?.ingredients || []).filter(i => i.name !== ingredient.name)
        }
      }
    }));
  };

  // Updates the quantity for a selected ingredient.
  const handleIngredientQuantityChange = (itemId, ingredientName, value) => {
    const ingredientInfo = selectedItem.extras.ingredients.find(i => i.name === ingredientName);
    setSelections(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        extras: {
          ...prev[itemId]?.extras || {},
          ingredients: (prev[itemId]?.extras?.ingredients || []).map(ingredient =>
            ingredient.name === ingredientName
              ? { ...ingredient, quantity: Math.max(1, parseInt(value)), price: ingredientInfo.price }
              : ingredient
          )
        }
      }
    }));
  };

  // Adjusts the overall quantity for a shawarma item.
  const handleQuantityChange = (itemId, delta) => {
    setSelections(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: Math.max(1, (prev[itemId]?.quantity || 1) + delta)
      }
    }));
  };

  // Adds the shawarma item (with selected ingredients and chef note) to the cart.
  const handleAddToCart = (item) => {
    const itemSelections = selections[item.id] || {};
    const cartItem = {
      id: `${item.id}-${Date.now()}`, // Unique cart item id
      name: item.name,
      price: item.price,
      quantity: itemSelections.quantity || 1,
      extras: itemSelections.extras || {},
      chefComment: chefComment
    };
    dispatch(addToCart(cartItem));
    toast.success(`${item.name} added to cart!`, {
      position: "top-right",
      autoClose: 3000
    });
  };

  // Renders each shawarma item card.
  const renderMenuItem = (item) => {
    const itemSelections = selections[item.id] || {};
    const quantity = itemSelections.quantity || 1;

    return (
      <div key={item.id} className="bg-black rounded-xl overflow-hidden shadow-lg border border-gray-800">
        <div className="relative">
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-48 object-cover"
          />
          <div className="absolute top-4 right-4">
            <div className="bg-black bg-opacity-50 px-3 py-1 rounded-full">
              <span className="bg-red-500 text-white font-medium text-lg py-1 px-3 rounded-full">
                ₦{item.price.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <h3 className="text-red-500 font-bold text-xl">{item.name}</h3>
            <p className="text-gray-400 mt-1 text-sm">{item.description}</p>
          </div>
          {/* Extras and Chef Note buttons in a row */}
          <div className="flex flex-row gap-2">
            <button
              onClick={() => handleOpenModal(item.id)}
              className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-600 flex items-center justify-center gap-2 transition-colors"
            >
              <Utensils className="w-4 h-4" />
              <span>Extras</span>
            </button>
            <button
              onClick={() => setIsCommentModalOpen(true)}
              className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-600 flex items-center justify-center gap-2 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Chef Note</span>
            </button>
          </div>
          {/* Quantity and Add to Cart controls arranged in a row */}
          <div className="flex flex-row gap-2 pt-2 items-center">
            <div className="flex items-center gap-2 bg-black rounded-lg p-1">
              <button
                onClick={() => handleQuantityChange(item.id, -1)}
                className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                disabled={quantity <= 1}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-medium text-red-500">{quantity}</span>
              <button
                onClick={() => handleQuantityChange(item.id, 1)}
                className="p-1 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => handleAddToCart(item)}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              disabled={!item.isAvailable}
            >
              <ShoppingCart className="w-4 h-4" />
              <span>{item.isAvailable ? 'Add to Cart' : 'Not Available'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-red-500">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {shawarmas.map(item => renderMenuItem(item))}
        </div>
      </div>

      {/* Ingredients Customization Modal */}
      {isModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Extras</h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {selectedItem.extras.ingredients.map((ingredient, idx) => (
                <div key={idx} className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={`${selectedItemId}-${ingredient.name}`}
                      checked={!!(selections[selectedItemId]?.extras?.ingredients?.find(i => i.name === ingredient.name))}
                      onChange={(e) => handleIngredientChange(selectedItemId, ingredient, e.target.checked)}
                      className="w-4 h-4 text-red-500 rounded border-gray-700 focus:ring-red-500 focus:ring-2"
                    />
                    <label htmlFor={`${selectedItemId}-${ingredient.name}`} className="text-gray-200">
                      {ingredient.name}
                      <span className="text-sm text-gray-400 block">₦{ingredient.price.toLocaleString()}</span>
                    </label>
                  </div>

                  {selections[selectedItemId]?.extras?.ingredients?.find(i => i.name === ingredient.name) && (
                    <input
                      type="number"
                      value={selections[selectedItemId]?.extras?.ingredients?.find(i => i.name === ingredient.name)?.quantity || 1}
                      min="1"
                      onChange={(e) => handleIngredientQuantityChange(selectedItemId, ingredient.name, e.target.value)}
                      className="w-16 p-1 bg-gray-800 border border-gray-600 rounded-lg text-center text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 transition-colors focus:ring-2 focus:ring-red-500"
              >
                <Check className="w-4 h-4" />
                <span>Done</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chef Note Modal */}
      {isCommentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Note for the Chef</h3>
              <button onClick={() => setIsCommentModalOpen(false)} className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={chefComment}
              onChange={(e) => setChefComment(e.target.value)}
              placeholder="Any special requests or preferences?"
              className="w-full h-32 p-3 bg-gray-800 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsCommentModalOpen(false)}
                className="px-6 py-2 border border-gray-600 text-gray-200 hover:bg-gray-800 rounded-lg transition-colors focus:ring-2 focus:ring-red-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (chefComment.trim()) {
                    setIsCommentModalOpen(false);
                  }
                }}
                className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 transition-colors focus:ring-2 focus:ring-red-500"
              >
                <Check className="w-4 h-4" />
                <span>Save Note</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShawarmaComponent;
